import { Filesystem } from '@capacitor/filesystem';
import { putBlob, getBlob, deleteBlob } from '~/utils/blobStore';
import { Network } from '@capacitor/network';
import { captureException } from '@sentry/capacitor';
import { ErrorWithBreadcrumb } from '~/errors';
import { Preferences } from '@capacitor/preferences';
import FileUpload, { type UploadProgress } from '~/native/FileUpload';
import type { PluginListenerHandle } from '@capacitor/core';
import { subDays, isAfter } from 'date-fns';
import { buildCompressibleMimeTypes } from '~/utils/fileUpload';

export const useFileUploadStore = defineStore('fileUpload', () => {
  const TIME_BETWEEN_UPLOADS = 1000;
  const MAX_UPLOAD_ATTEMPTS = 1;
  const QUEUE_STORAGE_KEY = 'file-upload-queue';
  const QUEUED_FILES_FOLDER = 'file-upload/files';

  const isUploading = computed<boolean>(() => queue.value.some(f => f.status === 'uploading'));
  let _isUploading: boolean = false;

  const fileApi = useFileAPI();
  const settingsStore = useSettingsStore();

  const compressibleMimeTypes = computed(() => buildCompressibleMimeTypes(settingsStore._settings.convertHeic));
  const { openToast } = useUserMessages();
  const { t, locale } = useEkI18n();
  const config = useRuntimeConfig();
  const tokenStore = useTokenStore();
  const platformStore = usePlatformStore();

  Network.addListener('networkStatusChange', async () => {
    if (await isUploadPossible()) {
      queue.value.forEach((f) => {
        f.status = 'uploading';
      });
    }
  });

  async function initialize() {
    try {
      queue.value = await getAllFiles();
      removeOldUploadedFiles();
    } catch (error) {
      captureException(error);
    }
  }

  function removeOldUploadedFiles() {
    const expiryDate = subDays(new Date(), 7);

    uploadedQueue.value = uploadedQueue.value.filter(f => {
      return isAfter(new Date(f.queuedAt), expiryDate);
    });
  }

  const queue = ref<(QAUploadMetadata | ProjectUploadMetadata)[]>([]);
  const QAQueue = computed(() => queue.value.filter(f => f.uploadType === 'qa'));
  const projectQueue = computed(() => queue.value.filter(f => f.uploadType === 'project'));
  const uploadedQueue = ref<(QAUploadMetadata | ProjectUploadMetadata)[]>([]);

  watch(queue, async (newQueue, oldQueue) => {
    saveQueueToPreferences(newQueue);

    const obsoleteFiles = oldQueue.filter(f => !newQueue.some(nf => nf.filePath === f.filePath));

    await Promise.all(obsoleteFiles.map(async (f) => {
      try {
        if (f.filePath) {
          await removeFileFromQueue(f.filePath);

          if (usePlatformStore().isWeb) {
            await deleteBlob(f.filePath);
          }
        }
      } catch (error) {
        captureException(error);
      }
    }));

    setTimeout(upload, TIME_BETWEEN_UPLOADS);
  }, { deep: true });

  async function saveQueueToPreferences(queueArr: (QAUploadMetadata | ProjectUploadMetadata)[]) {
    // Remove imageData before saving to Preferences
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const queueForStorage = queueArr.map(({ imageData, ...rest }) => rest);
    await Preferences.set({ key: QUEUE_STORAGE_KEY, value: JSON.stringify(queueForStorage) });
  }

  async function getFileData(params: QAUploadMetadata | ProjectUploadMetadata | EnqueueParams): Promise<Blob> {
    const blob = params.blob ?? (params.filePath !== undefined ? await getBlob(params.filePath) : undefined);

    if (blob === undefined) {
      openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Unable to enqueue file. Blob and path are undefined' });
    }

    return blob;
  }

  interface enqueueOptions {
    suppressLoading?: boolean;
  }

  function renameInUploadedQueue(oldFileName: string, newFileName: string) {
    const item = uploadedQueue.value.find(f => f.fileName === oldFileName);

    if (item) {
      item.fileName = newFileName;
    }
  }

  async function createWebFile(file: EnqueueParams): Promise<{ uri: string }> {
    if (file.blob === undefined) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Unable to create web file: blob is undefined' });
    }

    const key = [QUEUED_FILES_FOLDER, crypto.randomUUID()].join('/');
    await putBlob(key, file.blob);

    return { uri: key };
  }

  function sanitizeAddress(address: GeolocationDto | undefined) {
    return {
      StreetName: address?.StreetName ?? '',
      StreetNumber: address?.StreetNumber ?? '',
      Unit: address?.Unit ?? '',
      PostalCode: address?.PostalCode ?? '',
      City: address?.City ?? '',
      Country: address?.Country ?? '',
      CountryCode: address?.CountryCode ?? ''
    };
  }

  async function enqueue(params: EnqueueParams | EnqueueParams[], options: enqueueOptions = {}) {
    const {
      suppressLoading = false
    } = options;

    if (suppressLoading === false) {
      await showLoading(t('loading.uploadPrepare'));
    }

    const _params = Array.isArray(params) === true ? params : [params];

    const metadataList: (QAUploadMetadata | ProjectUploadMetadata)[] = [];

    for (const fileParams of _params) {
      const isProjectUpload = fileParams.projectId !== null && fileParams.folderId !== null;
      const isQAUpload = fileParams.qaAnswerBody !== null;

      if (isProjectUpload === false && isQAUpload === false) {
        openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Project ID and Folder ID or QA Answer Body ID must be provided' });
      }

      const filePath = usePlatformStore().isWeb ? (await createWebFile(fileParams)).uri : fileParams.filePath;

      if (!filePath) {
        openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'File path cannot be undefined' });
      }

      const uploadMetadata: UploadMetaData = {
        filePath: filePath,
        fileName: encodeURI(fileParams.name),
        fileMimeType: fileParams.mimeType,
        attempts: 0,
        status: 'uploading',
        blob: fileParams.blob,
        exif: fileParams.exif,
        address: fileParams.address,
        comment: fileParams.comment,
        projectId: fileParams.projectId,
        worksheetId: fileParams.worksheetId,
        queuedAt: new Date().toISOString()
      };

      let specificUploadMetadata: QAUploadMetadata | ProjectUploadMetadata;

      if (fileParams.projectId !== null && fileParams.folderId !== null) {
        specificUploadMetadata = {
          ...uploadMetadata,
          uploadType: 'project',
          folderId: fileParams.folderId
        };
      } else if (fileParams.qaAnswerBody !== null) {
        specificUploadMetadata = {
          ...uploadMetadata,
          uploadType: 'qa',
          qaAnswerBody: fileParams.qaAnswerBody
        };
      } else {
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Project ID and Folder ID or QA Answer Body ID must be provided' });
      }

      metadataList.push(specificUploadMetadata);
    }

    await addToQueue(metadataList);

    if (suppressLoading === false) {
      hideLoading();
    }

    openToast(t('documents.userMessages.filesHasBeenEnqueued', { count: _params.length }), ToastType.INFO);
  }

  async function isUploadPossible(): Promise<boolean> {
    const { connectionType, connected } = await Network.getStatus();
    const { uploadOnCellular } = settingsStore.settings;

    return connected && (connectionType === 'wifi' || (connectionType === 'cellular' && uploadOnCellular) || connectionType === 'unknown');
  }

  interface UploadProgressInfo { ratio: number; total: number }
  const progressMap = reactive<Record<string, UploadProgressInfo | undefined>>({});

  async function upload() {
    if (_isUploading) return;
    _isUploading = true;

    let uploadProgress: PluginListenerHandle | null = null;

    try {
      const canUpload = await isUploadPossible();

      if (!canUpload) {
        queue.value.forEach((f) => {
          if (f.status === 'uploading') f.status = 'awaitingNetwork';
        });

        return;
      }

      const metadata = queue.value.find(f => f.status === 'uploading');

      if (!metadata) return;

      // Native (Phone) Upload
      if (platformStore.isMobile) {
        uploadProgress = await FileUpload.addListener('progress', (p: UploadProgress) => {
          progressMap[metadata.fileName] = { ratio: p.ratio, total: p.total };
        });

        const { uploadUrl, projectId, folderId, qaAnswerBody, isCompressible } = getUploadConfig(metadata);
        const accessToken = await tokenStore.getAccessToken();
        const headers = {
          Authorization: `Bearer ${accessToken.token}`,
          localization: locale.value
        };

        const result = await FileUpload.uploadFile({
          path: metadata.filePath,
          uploadUrl,
          fileName: metadata.fileName,
          mimeType: metadata.fileMimeType,
          headers,
          projectId,
          qaAnswerBody,
          address: sanitizeAddress(metadata.address),
          fileFolderId: folderId,
          comment: metadata.comment,
          isCompressible
        });

        // Also verify the server-level result (HasErrors/Data) from the response body,
        // just like the web path does — HTTP 200 alone is not enough.
        let nativeSuccess = result.success;

        if (nativeSuccess && result.response) {
          try {
            const parsed = JSON.parse(result.response) as { HasErrors?: boolean; Data?: boolean };

            if (parsed.HasErrors === true || parsed.Data === false) {
              nativeSuccess = false;
            }
          } catch {
            // Not a JSON response — treat the HTTP status as authoritative.
          }
        }

        if (nativeSuccess) {
          const item = queue.value.find(file => file.fileName === metadata.fileName);

          if (item) {
            item.status = 'uploaded';
            uploadedQueue.value.push(item);
          }

          queue.value = queue.value.filter(file => file.fileName !== metadata.fileName);
        } else {
          const reason = `Native upload failed: success=${result.success} code=${result.code} response=${(result.response ?? '').slice(0, 1000)}`;
          captureException(new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: reason }));
          handleUploadFailure(metadata);
        }

        // Web Upload
      } else {
        const fileData = await getFileData(metadata);

        const onProgress = (ratio: number, total: number) => {
          progressMap[metadata.fileName] = { ratio, total };
        };

        let isUploaded = false;

        if (metadata.uploadType === 'project') {
          isUploaded = await fileApi.uploadToProject(fileData, metadata.fileMimeType, metadata.fileName, metadata.address, metadata.comment, metadata.projectId, metadata.folderId, onProgress);
        } else if (metadata.uploadType === 'qa') {
          isUploaded = await fileApi.uploadToQA(fileData, metadata.fileMimeType, metadata.fileName, metadata.address, metadata.comment, metadata.qaAnswerBody, onProgress);
        } else {
          throw new ErrorWithBreadcrumb({
            category: BreadCrumbCategoryEnum.FileUpload,
            message: 'Invalid upload type'
          });
        }

        if (isUploaded) {
          const item = queue.value.find(file => file.fileName === metadata.fileName);

          if (item) {
            item.status = 'uploaded';
            uploadedQueue.value.push(item);
          }

          queue.value = queue.value.filter(file => file.fileName !== metadata.fileName);
        } else {
          handleUploadFailure(metadata);
        }
      }

    } catch (error) {
      queue.value.forEach(f => f.status = 'failed');
      captureException(error);
    } finally {
      uploadProgress?.remove();
      _isUploading = false;
    }
  }

  function handleUploadFailure(metadata: QAUploadMetadata | ProjectUploadMetadata) {
    metadata.attempts += 1;
    openToast(t('fileUpload.userMessage.error', { fileName: decodeURI(metadata.fileName) }), ToastType.ERROR);

    if (metadata.attempts >= MAX_UPLOAD_ATTEMPTS) {
      metadata.status = 'failed';
      queue.value = queue.value.filter(file => file.fileName !== metadata.fileName);
    }
  }

  function getUploadConfig(metadata: QAUploadMetadata | ProjectUploadMetadata) {
    const isCompressible = compressibleMimeTypes.value.has(metadata.fileMimeType);
    const endpoint = isCompressible ? 'StreamUploadCompressible' : 'StreamUploadNonCompressible';

    if (metadata.uploadType === 'project') {
      return {
        uploadUrl: `${config.public.apiBase}V4/File/${endpoint}`,
        projectId: metadata.projectId,
        folderId: metadata.folderId,
        isCompressible
      };
    }

    if (metadata.uploadType === 'qa') {
      return {
        uploadUrl: `${config.public.apiBase}V4/File/${endpoint}`,
        qaAnswerBody: metadata.qaAnswerBody,
        folderId: undefined,
        isCompressible
      };
    }

    throw new ErrorWithBreadcrumb({
      category: BreadCrumbCategoryEnum.FileUpload,
      message: 'Invalid upload type'
    });
  }

  function clearFailedForProject(projectId: number) {
    queue.value = queue.value.filter(f => !(f.status === 'failed' && f.uploadType === 'project' && f.projectId === projectId));
  }

  function retry(file: UploadMetaData) {
    const metadata = queue.value.find(f => f.filePath === file.filePath);

    if (metadata) {
      metadata.status = 'uploading';
      metadata.attempts = 0;
    }
  }

  // Handle local storage of the queue
  async function setImageData(file: QAUploadMetadata | ProjectUploadMetadata) {
    try {
      if (platformStore.isWeb) {
        const blob = await getBlob(file.filePath);
        file.imageData = blob !== undefined ? URL.createObjectURL(blob) : undefined;
      } else {
        // Only load image files. Filesystem.readFile encodes the entire file as
        // base64 in the JS heap (~1.33× the file size) before returning — calling
        // it on a large video (e.g. 200 MB) OOM-crashes the WebView immediately,
        // and then again on every restart while the item sits in the queue.
        // Non-image files never render a thumbnail anyway (UploadItem v-if="isImage").
        if (file.fileMimeType?.startsWith('image/')) {
          const fileData = await Filesystem.readFile({ path: file.filePath });
          file.imageData = base64ToDataUrl(fileData.data.toString(), file.fileMimeType);
        } else {
          file.imageData = undefined;
        }
      }
    } catch (error) {
      captureException(error);
      file.imageData = undefined;
    }
  }

  async function addToQueue(fileMetadata: QAUploadMetadata | ProjectUploadMetadata | (QAUploadMetadata | ProjectUploadMetadata)[]) {
    const files = Array.isArray(fileMetadata) ? fileMetadata : [fileMetadata];

    await Promise.all(files.map(setImageData));

    queue.value.push(...files);
    saveQueueToPreferences(queue.value);
  }

  async function getAllFiles() {
    const storedQueue = await Preferences.get({ key: QUEUE_STORAGE_KEY });
    queue.value = storedQueue.value ? JSON.parse(storedQueue.value) : [];

    await Promise.all(queue.value.map(setImageData));

    return queue.value;
  }

  async function removeFileFromQueue(filePath: string) {
    queue.value = queue.value.filter(file => file.filePath !== filePath);
    saveQueueToPreferences(queue.value);
  }

  return {
    initialize,
    queue,
    QAQueue,
    projectQueue,
    uploadedQueue,
    enqueue,
    retry,
    clearFailedForProject,
    isUploading,
    progressMap,
    removeOldUploadedFiles,
    renameInUploadedQueue
  };
});
